import { z } from 'zod';
import { basisPoints, currency, email, id, isoDate, moneyMinor, nonEmpty, optionalText, phone } from './common';
import { TRACKING_STATUSES } from './rules';

// ---------- Logistics partners (carriers, couriers, 3PL) ----------
export const LOGISTICS_PARTNER_CATEGORIES = ['courier', 'freight_forwarder', 'transporter', '3pl_warehouse', 'other'] as const;
export const INTEGRATION_METHODS = ['manual', 'api', 'webhook'] as const;

export const logisticsPartnerRequest = z.object({
  partnerCode: z.string().trim().min(2).max(20).regex(/^[A-Za-z0-9_-]+$/, 'Letters, digits, - and _ only'),
  name: nonEmpty(120),
  category: z.enum(LOGISTICS_PARTNER_CATEGORIES),
  serviceTypes: z.array(z.string().trim().min(1).max(40)).max(12).default([]),
  contactName: optionalText(120),
  email: email.nullable().optional(),
  phone: phone.nullable().optional(),
  billingAddress: optionalText(500),
  taxId: z.string().trim().max(20).regex(/^[A-Za-z0-9]*$/, 'Letters and digits only').nullable().optional(),
  coverage: z.enum(['domestic', 'international', 'both']).default('domestic'),
  servicesDescription: optionalText(1000),
  accountReference: optionalText(80),
  paymentTermsDays: z.number().int().min(0).max(365).nullable().optional(),
  defaultService: optionalText(60),
  notes: optionalText(2000),
  integrationMethod: z.enum(INTEGRATION_METHODS).default('manual'),
  /** Public tracking page template with "{tracking}" placeholder; not a credential. */
  trackingUrlTemplate: z
    .string()
    .trim()
    .max(300)
    .refine((v) => v === '' || (/^https:\/\//.test(v) && v.includes('{tracking}')), 'Use an https:// URL containing {tracking}')
    .nullable()
    .optional(),
});
export const updateLogisticsPartnerRequest = logisticsPartnerRequest.extend({ version: z.number().int().positive() });
export const logisticsPartnerStatusRequest = z.object({ status: z.enum(['active', 'inactive']), version: z.number().int().positive() });

// ---------- Shipment logistics details and tracking ----------
export const shipmentLogisticsRequest = z.object({
  logisticsPartnerId: id.nullable().optional(),
  serviceType: optionalText(60),
  trackingNumber: optionalText(80),
  expectedDeliveryDate: isoDate.nullable().optional(),
  podRequired: z.boolean().optional(),
  ackRequired: z.boolean().optional(),
});

export const trackingEventRequest = z.object({
  status: z.enum(TRACKING_STATUSES),
  /** When the milestone happened (ISO date-time); defaults to now. Future times are rejected. */
  occurredAt: z.string().datetime({ offset: true }).optional(),
  location: optionalText(120),
  description: optionalText(500),
  /** Carrier's event id when copied from a carrier system; makes re-entry idempotent. */
  externalEventId: z.string().trim().max(120).nullable().optional(),
  exceptionReason: optionalText(300),
});

// ---------- Freight ----------
export const freightChargeRequest = z
  .object({
    arrangement: z.enum(['customer_pays_carrier', 'company_pays_recovers', 'company_absorbs']),
    logisticsPartnerId: id.nullable().optional(),
    currency,
    expectedCarrierMinor: moneyMinor.nullable().optional(),
    customerChargeMinor: moneyMinor.default(0),
    customerBilling: z.enum(['none', 'on_order_invoice', 'separate_invoice']).default('none'),
    customerBillingRef: optionalText(80),
    notes: optionalText(1000),
  })
  .superRefine((x, ctx) => {
    if (x.arrangement === 'customer_pays_carrier' && (x.customerChargeMinor > 0 || x.customerBilling !== 'none')) {
      ctx.addIssue({ code: 'custom', path: ['customerBilling'], message: 'When the customer pays the carrier directly there is no customer freight charge from us' });
    }
    if (x.arrangement === 'company_absorbs' && (x.customerChargeMinor > 0 || x.customerBilling !== 'none')) {
      ctx.addIssue({ code: 'custom', path: ['customerChargeMinor'], message: 'Absorbed freight has no customer charge; choose "recharge customer" instead' });
    }
    if (x.arrangement === 'company_pays_recovers' && (x.customerChargeMinor <= 0 || x.customerBilling === 'none')) {
      ctx.addIssue({ code: 'custom', path: ['customerChargeMinor'], message: 'Enter the customer freight charge and how it is billed' });
    }
  });
export const updateFreightChargeRequest = z.object({ version: z.number().int().positive() }).and(freightChargeRequest);

export const freightCollectionRequest = z.object({
  requestKey: z.string().min(8).max(80),
  amountMinor: moneyMinor.refine((n) => n > 0, 'Amount must be positive'),
  collectedDate: isoDate,
  method: z.enum(['bank_transfer', 'upi', 'cheque', 'cash', 'card', 'other']),
  reference: optionalText(120),
});

export const carrierInvoiceRequest = z
  .object({
    logisticsPartnerId: id,
    invoiceNumber: nonEmpty(60),
    invoiceDate: isoDate,
    dueDate: isoDate.nullable().optional(),
    currency,
    taxableMinor: moneyMinor,
    taxMinor: moneyMinor.default(0),
    notes: optionalText(1000),
    /** Amounts billed per freight charge (incl. tax). Their sum may not exceed the invoice total. */
    lines: z.array(z.object({ freightChargeId: id, amountMinor: moneyMinor.refine((n) => n > 0, 'Amount must be positive') })).max(200).default([]),
  })
  .refine((x) => !x.dueDate || x.dueDate >= x.invoiceDate, { path: ['dueDate'], message: 'Due date cannot be before the invoice date' });

export const carrierInvoiceActionRequest = z.object({ version: z.number().int().positive(), comment: optionalText(500) });

export const carrierPaymentRequest = z.object({
  requestKey: z.string().min(8).max(80),
  logisticsPartnerId: id,
  amountMinor: moneyMinor.refine((n) => n > 0, 'Amount must be positive'),
  currency,
  paymentDate: isoDate,
  method: z.enum(['bank_transfer', 'upi', 'cheque', 'cash', 'card', 'other']),
  reference: optionalText(120),
  notes: optionalText(1000),
  allocations: z.array(z.object({ carrierInvoiceId: id, amountMinor: moneyMinor.refine((n) => n > 0) })).max(50).default([]),
});
export const carrierPaymentReversalRequest = z.object({ version: z.number().int().positive(), reason: nonEmpty(500) });

export const discrepancyResolutionRequest = z.object({
  status: z.enum(['accepted', 'disputed', 'resolved']),
  reason: optionalText(500),
  resolution: optionalText(1000),
  version: z.number().int().positive(),
});

export const LOGISTICS_DOCUMENT_KINDS = [
  'carrier_invoice',
  'carrier_payment_receipt',
  'customer_payment_ack',
  'proof_of_delivery',
  'delivery_ack',
  'freight_quotation',
  'credit_note',
  'other',
] as const;
export const LOGISTICS_ATTACHMENT_ENTITIES = ['shipment', 'carrier_invoice', 'carrier_payment', 'freight_charge'] as const;
export const logisticsAttachmentMetadata = z.object({
  entityType: z.enum(LOGISTICS_ATTACHMENT_ENTITIES),
  entityId: id,
  kind: z.enum(LOGISTICS_DOCUMENT_KINDS),
  notes: optionalText(500),
});
export const attachmentReviewRequest = z.object({ decision: z.enum(['verified', 'rejected']), comment: optionalText(500) });

// ---------- Alert windows ----------
export const alertSettingsRequest = z.object({
  paymentDueSoonDays: z.number().int().min(0).max(90),
  carrierDueSoonDays: z.number().int().min(0).max(90),
  deliveryRiskDays: z.number().int().min(0).max(30),
  trackingStaleDays: z.number().int().min(1).max(30),
  expiryWarningDays: z.number().int().min(1).max(730),
  creditWarningBp: basisPoints,
});
